import { useSyncExternalStore } from 'react';

import { en } from './strings/en';

/**
 * Two languages, no library.
 *
 * react-i18next is ~40 kB for interpolation, a plural rule and a context provider. This
 * console needs the first two and gets the third from a module-level variable, so what is
 * here is the whole mechanism: one dictionary, a lookup, and a re-render.
 *
 * The German text is the key. The alternative — invented keys like `sites.empty.hint` and
 * two parallel dictionaries — doubles the writing, and every key is a second name for a
 * sentence that already exists. Here the call site still reads as the sentence it renders,
 * `en.ts` is the only file that has to be written, and a key with no English entry falls
 * back to German: visible in an English console, greppable, and never a blank button.
 *
 * The cost is that editing a German string silently orphans its English entry. That is
 * what `i18n.test.ts` is for — it reads every `t('…')` in the source and fails on any that
 * `en.ts` does not answer.
 *
 * German stays the default. This is a product about the DSGVO sold to German customers; an
 * English console is for the people running it, not for the market.
 */
export type Lang = 'de' | 'en';

const STORAGE = 'cmp_lang';

/**
 * The stored choice wins over the browser, and anything unknown falls back to German.
 *
 * `navigator.language` is read only when nothing is stored: someone who switched to
 * English once should not be switched back by opening the console on a colleague's
 * German-configured laptop.
 */
function detect(): Lang {
  const stored = localStorage.getItem(STORAGE);
  if (stored === 'de' || stored === 'en') return stored;
  return navigator.language.toLowerCase().startsWith('en') ? 'en' : 'de';
}

let current: Lang = detect();
const listeners = new Set<() => void>();

export const getLang = (): Lang => current;

export function setLang(l: Lang): void {
  if (l === current) return;
  current = l;
  localStorage.setItem(STORAGE, l);
  listeners.forEach((f) => f());
}

function subscribe(f: () => void): () => void {
  listeners.add(f);
  return () => { listeners.delete(f); };
}

/**
 * Subscribed once, at the root, and used as a remount key.
 *
 * The alternative is every component that prints a word subscribing to the language: a
 * hook in ~40 components, and one forgotten hook away from a screen that stays German
 * after the switch. Remounting costs the local state of whatever is open — a half-filled
 * invite form, the selected banner section — and switching language is a rare, deliberate
 * act, so that is the cheaper mistake.
 *
 * The banner draft survives it: edits are autosaved and the store is rebuilt from the
 * server on mount. The undo history does not, which is why the switch sits in the header
 * and not next to the editor.
 */
export const useLang = (): Lang => useSyncExternalStore(subscribe, getLang);

/**
 * The German source string, with `{name}` placeholders substituted.
 *
 * Under `de` this is the identity function plus interpolation, so the German console pays
 * nothing for the English one existing.
 */
export function t(source: string, vars?: Record<string, string | number>): string {
  const s = current === 'en' ? en[source] ?? source : source;
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole);
}

/**
 * Singular or plural, by count.
 *
 * Both languages here need exactly two forms, so this takes two strings rather than
 * growing a plural-rule table for languages the console does not have. `{n}` is filled in
 * for free, because every caller wanted it.
 */
export function tn(one: string, other: string, n: number, vars?: Record<string, string | number>): string {
  return t(n === 1 ? one : other, { n, ...vars });
}

/**
 * The locale for everything Intl formats.
 *
 * Dates and numbers were formatted with a hardcoded 'de-DE' in nine places. In an English
 * console that produces "13. August 2026" under an English heading, which reads as a bug
 * in the date rather than as a language setting.
 */
export const locale = (): string => (current === 'en' ? 'en-GB' : 'de-DE');

export const fmtDate = (d: string | number | Date): string =>
  new Date(d).toLocaleDateString(locale());

export const fmtDateTime = (d: string | number | Date): string =>
  new Date(d).toLocaleString(locale());

export const fmtNum = (n: number): string => n.toLocaleString(locale());

/**
 * A percentage with one decimal.
 *
 * The old helper did `toFixed(1).replace('.', ',')` — a hand-rolled German decimal comma
 * that produces "12,5 %" in an English console. Intl knows the separator for both.
 */
export const fmtPct = (v: number): string =>
  `${v.toLocaleString(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
